import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import { useDispatch, useSelector } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { State } from '../../../Flux/Slice/index.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { UnrestorableDraft } from '../../../Services/OrderWizard/draftStorage.ts'
import { trackWizardContinuedInClassic } from '../../../Services/OrderWizard/telemetry.ts'
import { plural } from '../../../Util/plural.ts'
import { Callout } from './Primitives.tsx'
import { RETRY_BUTTON } from './retryStyle.ts'

type RootState = typeof State

const LINK = `${RETRY_BUTTON} px-0`

export const Notice = ({ children, testId }: { children: ReactNode; testId?: string }) => (
  <Callout className="flex items-start gap-2 rounded-lg bg-white px-3 py-2 text-sm text-slate-700" role="alert" data-testid={testId}>
    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
    <span>{children}</span>
  </Callout>
)

const ResumeButton = ({ orderId, label = 'Resume in the wizard' }: { orderId: string; label?: string }) => {
  const dispatch = useDispatch()
  return (
    <button type="button" className={LINK} data-testid="wizard-resume-order" onClick={() => dispatch(wizard.resumeRequested({ orderId }))}>
      {label}
    </button>
  )
}

const ClassicLink = ({ orderId }: { orderId: string }) => (
  <Link className="link" to={`/order/${orderId}`} onClick={() => trackWizardContinuedInClassic('unrestorable')}>
    classic view
  </Link>
)

/** Stored drafts the wizard could not bring back, unreadable or not opened for too long, each with its order. */
export const UnrestorableNotice = ({ entries }: { entries: UnrestorableDraft[] }) => {
  if (entries.length === 0) {
    return null
  }
  const expired = entries.filter((entry) => entry.reason === 'expired')
  const unreadable = entries.filter((entry) => entry.reason !== 'expired')
  const lead = [
    expired.length > 0 && `${expired.length === 1 ? 'A wizard draft' : plural(expired.length, 'wizard draft')} not opened for two weeks ${expired.length === 1 ? 'was' : 'were'} cleared`,
    unreadable.length > 0 && `${unreadable.length === 1 ? 'A saved wizard draft' : plural(unreadable.length, 'saved wizard draft')} could not be restored`
  ]
    .filter(Boolean)
    .join('; ')
  const withOrder = entries.filter((entry) => entry.orderId)
  return (
    <Notice testId="wizard-unrestorable">
      {lead}
      {withOrder.length > 0 ? '. Their orders are kept:' : '.'}
      {withOrder.map((entry) => (
        <span key={entry.draftId} className="block" data-testid="wizard-unrestorable-order">
          order {entry.orderId?.slice(0, 8)} — <ResumeButton orderId={entry.orderId!} /> or continue in the <ClassicLink orderId={entry.orderId!} />
        </span>
      ))}
    </Notice>
  )
}

/** After a discard: the order, if one was made, can be resumed or deleted, never both silently. */
export const DiscardedNotice = () => {
  const dispatch = useDispatch()
  const discarded = useSelector((state: RootState) => state.orderWizard.discarded)
  if (!discarded) {
    return null
  }
  const { name, orderId, deletion } = discarded
  const dismiss = (
    <button type="button" className={`${LINK} ml-1`} data-testid="wizard-discarded-dismiss" onClick={() => dispatch(wizard.discardedNoticeDismissed())}>
      Dismiss
    </button>
  )
  return (
    <Notice testId="wizard-discarded">
      Draft “{name}” discarded.
      {orderId && !deletion && (
        <>
          {' '}
          Its order stays under My Orders: <ResumeButton orderId={orderId} label="Resume" /> ·{' '}
          <button
            type="button"
            className={`${LINK} text-red-700`}
            data-testid="wizard-discarded-delete"
            onClick={() => dispatch(wizard.discardedOrderDeleteRequested({ orderId }))}
          >
            Delete the order too
          </button>
        </>
      )}
      {deletion?.status === 'deleting' && ' Deleting its order…'}
      {deletion?.status === 'deleted' && ' Its order was deleted.'}
      {deletion?.status === 'failed' && ` The order was not deleted: ${deletion.error ?? 'unknown error'}`}
      {deletion?.status !== 'deleting' && dismiss}
    </Notice>
  )
}

/** An order being resumed, or why it could not be. */
export const ResumeStatus = () => {
  const dispatch = useDispatch()
  const resume = useSelector((state: RootState) => state.orderWizard.resume)
  if (!resume) {
    return null
  }
  if (resume.status === 'loading') {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-600" data-testid="wizard-resuming">
        <span className="loading loading-spinner loading-sm" /> Opening the order in the wizard…
      </p>
    )
  }
  return (
    <Notice testId="wizard-resume-failed">
      {resume.error}{' '}
      <button type="button" className={LINK} onClick={() => dispatch(wizard.resumeNoticeDismissed())}>
        Dismiss
      </button>
    </Notice>
  )
}

/** On an empty start screen: the lab's unsubmitted orders no draft holds, to carry on with one of them. */
export const ResumableOrders = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  const orders = useSelector((state: RootState) => state.orderWizard.resumable)
  const fresh = !draft.orderId && draft.fileOrder.length === 0
  useEffect(() => {
    if (fresh) {
      dispatch(wizard.resumableOrdersRequested())
    }
  }, [fresh, dispatch])
  if (!fresh || !orders?.length) {
    return null
  }
  return (
    <div className="text-sm text-slate-600" data-testid="wizard-resumable">
      <p className="font-medium text-slate-700">Or continue an unsubmitted order:</p>
      <ul className="mt-1 space-y-0.5">
        {orders.map((order) => (
          <li key={order.orderId} className="flex flex-wrap items-center gap-x-1.5" data-testid="wizard-resumable-order">
            <span className="text-slate-800">{order.name}</span>
            {order.createdAt && <span className="text-xs text-slate-500">{new Date(order.createdAt).toLocaleDateString()}</span>}
            <ResumeButton orderId={order.orderId} label="Resume" />
          </li>
        ))}
      </ul>
    </div>
  )
}
